import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/** How long the "added to cart" confirmation stays in the drawer header. */
const ADDED_CONFIRMATION_DURATION = 4000;

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #focusTrapAbortController = null;

  /** @type {number | undefined} */
  #addedResetTimer;

  /**
   * Element that should regain focus when the drawer closes. Captured from
   * document.activeElement on open; openers whose flow loses focus before the
   * drawer opens (the ATC button is disabled during the /cart/add fetch, so
   * focus falls to <body>) can set it explicitly via `returnFocusTo`.
   * @type {HTMLElement | null}
   */
  #returnFocusTo = null;

  /** @param {HTMLElement | null} el */
  set returnFocusTo(el) {
    this.#returnFocusTo = el instanceof HTMLElement ? el : null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    // Scroll-lock the page while the drawer is open. The base DialogComponent already
    // pins <body> (position: fixed) + traps focus via native showModal(); this mirrors the
    // menu drawer's html[scroll-lock] mechanism so overflow is unambiguously frozen too.
    this.addEventListener(DialogOpenEvent.eventName, this.#lockScroll);
    this.addEventListener(DialogCloseEvent.eventName, this.#unlockScroll);
    // Harden the focus wrap: showModal() alone lets Tab escape to <body> after
    // embedded payment iframes (e.g. PayPal express button).
    this.addEventListener(DialogOpenEvent.eventName, this.#setupFocusTrap);
    this.addEventListener(DialogCloseEvent.eventName, this.#teardownFocusTrap);
    this.addEventListener(DialogCloseEvent.eventName, this.#hideAddedConfirmation);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.removeEventListener(DialogOpenEvent.eventName, this.#lockScroll);
    this.removeEventListener(DialogCloseEvent.eventName, this.#unlockScroll);
    this.removeEventListener(DialogOpenEvent.eventName, this.#setupFocusTrap);
    this.removeEventListener(DialogCloseEvent.eventName, this.#teardownFocusTrap);
    this.removeEventListener(DialogCloseEvent.eventName, this.#hideAddedConfirmation);
    this.#unlockScroll();
    this.#teardownFocusTrap();
    clearTimeout(this.#addedResetTimer);
    this.#historyAbortController?.abort();
  }

  /**
   * Captures a focus-restore target right before the native showModal() moves
   * focus into the dialog (Escape/backdrop close would otherwise land on body).
   */
  showDialog() {
    const active = document.activeElement;
    if (!this.#returnFocusTo && active instanceof HTMLElement && active !== document.body && !this.contains(active)) {
      this.#returnFocusTo = active;
    }
    super.showDialog();
  }

  #restoreFocus = () => {
    const target = this.#returnFocusTo;
    this.#returnFocusTo = null;
    if (!target || !target.isConnected) return;
    if (target instanceof HTMLButtonElement && target.disabled) return;
    try {
      target.focus({ preventScroll: true });
    } catch {
      /* noop — focus restoration is best-effort */
    }
  };

  #lockScroll = () => {
    document.documentElement.setAttribute('scroll-lock', '');
  };

  #unlockScroll = () => {
    document.documentElement.removeAttribute('scroll-lock');
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /** @param {Event} event */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Success moment only for real additions, not for quantity updates or
    // removals, which share the same 'cart:update' event name. Additions come
    // either as CartAddEvent (product-form-component: quick add, cross-sell)
    // or as the plain CustomEvent dispatched by the th-product-info ATC flow.
    const detail = /** @type {CustomEvent} */ (event).detail;
    const isAddition = event instanceof CartAddEvent || detail?.data?.source === 'th-product-info';
    if (isAddition && !detail?.data?.didError) {
      this.#showAddedConfirmation();
    }
  };

  /**
   * Briefly swaps the drawer heading for a green "In den Warenkorb gelegt"
   * confirmation (benchmark: snocks). Deferred by two frames so it runs after
   * the section morph triggered by the same cart event (the morph re-syncs
   * attributes from server HTML and would strip the state class) and after
   * showModal() from the auto-open flow.
   */
  #showAddedConfirmation() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.refs.dialog?.open) return;

        this.classList.add('cart-drawer--show-added');
        this.#announce('In den Warenkorb gelegt');

        clearTimeout(this.#addedResetTimer);
        this.#addedResetTimer = setTimeout(this.#hideAddedConfirmation, ADDED_CONFIRMATION_DURATION);
      });
    });
  }

  #hideAddedConfirmation = () => {
    clearTimeout(this.#addedResetTimer);
    this.classList.remove('cart-drawer--show-added');
  };

  /**
   * Announces a message to screen readers via the drawer's live region.
   * Clear-then-set so repeated additions re-announce reliably.
   * @param {string} message
   */
  #announce(message) {
    const region = this.refs.cartAddedLiveRegion;
    if (!(region instanceof HTMLElement)) return;

    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /**
   * Keeps Tab cycling inside the open drawer. Native showModal() constrains
   * focus, but tabbing out of an embedded payment iframe (PayPal) can drop
   * focus onto <body> for a beat. Three layers:
   * 1. keydown wrap for regular elements (last -> first, first -> last),
   * 2. zero-size sentinel tabbables around the dialog content that catch
   *    exits keydown cannot see (iframes handle Tab internally),
   * 3. a document focusin fallback that pulls escaped focus back in.
   */
  #setupFocusTrap = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#focusTrapAbortController?.abort();
    this.#focusTrapAbortController = new AbortController();
    const { signal } = this.#focusTrapAbortController;

    dialog.addEventListener('keydown', this.#handleTrapKeydown, { signal });
    document.addEventListener('focusin', this.#handleTrapFocusIn, { signal });
  };

  #teardownFocusTrap = () => {
    this.#focusTrapAbortController?.abort();
    this.#focusTrapAbortController = null;
  };

  /** @returns {HTMLElement[]} Tabbable elements inside the dialog, sentinels excluded. */
  #getTabbable() {
    const { dialog } = this.refs;
    if (!dialog) return [];

    const candidates = dialog.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    );

    return Array.from(candidates).filter(
      (el) =>
        el instanceof HTMLElement &&
        !el.hasAttribute('data-focus-sentinel') &&
        (typeof el.checkVisibility !== 'function' || el.checkVisibility())
    );
  }

  /** @param {KeyboardEvent} event */
  #handleTrapKeydown = (event) => {
    if (event.key !== 'Tab') return;

    const tabbable = this.#getTabbable();
    if (tabbable.length === 0) return;

    const first = tabbable[0];
    const last = tabbable[tabbable.length - 1];
    const active = document.activeElement;

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    } else if (event.shiftKey && (active === first || active === this.refs.dialog)) {
      event.preventDefault();
      last?.focus();
    }
  };

  /** @param {FocusEvent} event */
  #handleTrapFocusIn = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (dialog.contains(target)) {
      // A sentinel means focus is about to leave the content: wrap it.
      const sentinel = target.getAttribute('data-focus-sentinel');
      if (!sentinel) return;

      const tabbable = this.#getTabbable();
      if (tabbable.length === 0) return;

      // Both sentinels forward to the first real element: the end sentinel is
      // reached by tabbing forward past the last element (or out of an iframe)
      // and wraps around; the start sentinel is only reachable when focus
      // re-enters from outside — backwards wrapping from the first element is
      // already intercepted on keydown.
      tabbable[0]?.focus();
      return;
    }

    // Focus escaped the modal entirely (e.g. after a payment iframe): pull it back.
    const tabbable = this.#getTabbable();
    (tabbable[0] ?? dialog).focus();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
